<script setup>
import { computed } from 'vue'
import useMediaQuery from '@convenia/composables/useMediaQuery'
import CButton from '@convenia/components/CButton'
import CTypo from '@convenia/components/CTypo'
import CCharacter from '@convenia/components/CCharacter'
import FormFieldValidation from '@convenia/components/CFormBuilder/fragments/FormFieldValidation.vue'

const { isMobile } = useMediaQuery()

const props = defineProps({
  dashed: {
    type: Boolean,
    default: true,
  },

  addDisabled: {
    type: Boolean,
    default: false
  },

  addText: {
    type: String,
    default: 'Adicionar'
  },

  emptyStateText: {
    type: String,
    default: ''
  },

  characterType: {
    type: String,
    default: 'photo'
  },

  addIcon: {
    type: [ String, null ],
    default: null
  },

  errorMessage: {
    type: String,
    default: null
  },

  addWidth: {
    type: Number,
    default: 180
  },

  extensions: {
    type: Array,
    default: () => []
  },
})

defineEmits([ 'add' ])

const allowedExtensions = computed(() => {
  const { extensions } = props

  if (!extensions.length) return ''

  const formatted = extensions.map(extension => `.${extension.toUpperCase()}`)
  const last = formatted.at(-1)
  const rest = formatted.slice(0, -1)

  return rest.length ? `${rest.join(', ')} e ${last}` : last
})

const infoText = computed(() =>
  props.emptyStateText ||
  `Aceitamos arquivos nos formatos ${allowedExtensions.value}.`
)
</script>

<script>
export default { name: 'CUploadImageEmptyList' }
</script>

<template>
  <div
    class="c-upload-image-empty-list"
    :class="{ '-dashed': dashed }"
  >
    <c-character
      :type="characterType"
      :size="isMobile ? 130 : 150"
    />

    <c-typo
      v-if="!isMobile"
      class="drag-text"
      type="h2"
      weight="medium"
      opacity="base-80"
    >
      Arraste seu arquivo e solte aqui
    </c-typo>

    <div
      v-if="!isMobile"
      class="separator"
    >
      <span class="line" />

      <c-typo
        class="label"
        type="body-2"
        opacity="base-50"
        weight="semibold"
      >
        OU
      </c-typo>

      <span class="line" />
    </div>

    <c-button
      data-testid="alternative-add-button"
      primary
      :width="addWidth"
      :icon="addIcon"
      :icon-size="isMobile ? 24 : 20"
      :disabled="addDisabled"
      @click.prevent="$emit('add')"
    >
      {{ addText }}
    </c-button>

    <div class="info-group">
      <c-typo
        class="info-text"
        type="body-1"
        opacity="base-30"
      >
        {{ infoText }}
      </c-typo>

      <form-field-validation
        v-if="errorMessage"
        class="validation"
        :message="errorMessage"
      />
    </div>
  </div>
</template>

<style lang="scss">
.c-upload-image-empty-list {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 50px 0;
  gap: 20px;
  overflow: hidden;

  @include responsive(xs-mobile, mobile) {
    gap: 10px;
    padding: 20px 0;
  }

  &.-dashed {
    border-radius: 5px;
    border: 1px dashed color-var(text, base-20);
  }

  & > .drag-text {
    line-height: 19px;
    word-wrap: break-word;
  }

  & > .separator {
    display: flex;
    align-items: center;
    gap: 15px;

    & > .line {
      width: 40px;
      height: 1px;
      background-color: color-var(text, base-10);
    }

    & > .label {
      font-size: 11px;
      word-wrap: break-word;
    }
  }

  & > .info-group {
    display: flex;
    flex-direction: column;
    align-items: center;

    & > .info-text {
      max-width: 264px;
      line-height: 19px;
      word-wrap: break-word;
    }
  }
}
</style>
